import axios from 'axios';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { CONFIG } from 'variables/config';

const UpdateBuyerInvoiceInformation = () => {
    const [user, setUser] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                setUser(user);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const fetchInvoices = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        setLoadingInvoices(true);
        axios
            .get(`${CONFIG.apiUrl}/stripe/invoices`, config)
            .then((res) => {
                setInvoices(res.data.invoices || []);
                setLoadingInvoices(false);
            })
            .catch((err) => {
                console.log(err);
                toast.error('Erreur lors de la récupération des factures');
                setLoadingInvoices(false);
            });
    };

    const handleChange = (e: any) => {
        const { id, value } = e.target;
        setUser({ ...user, [id]: value });
    };

    const updateProfil = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        const data = {
            company: user.company,
            address: user.address,
            siren: user.siren,
        };
        axios
            .put(`${CONFIG.apiUrl}/user`, data, config)
            .then((res) => {
                toast.success('Votre profil a été mis à jour');
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        initUser();
        fetchInvoices();
    }, []);

    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getStatusBadge = (status: string) => {
        const statusColors: { [key: string]: string } = {
            paid: 'bg-green-100 text-green-800',
            open: 'bg-yellow-100 text-yellow-800',
            draft: 'bg-gray-100 text-gray-800',
            void: 'bg-red-100 text-red-800',
            uncollectible: 'bg-red-100 text-red-800',
        };
        const statusLabels: { [key: string]: string } = {
            paid: 'Payée',
            open: 'En attente',
            draft: 'Brouillon',
            void: 'Annulée',
            uncollectible: 'Impayée',
        };
        return (
            <span
                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                    statusColors[status] || 'bg-gray-100 text-gray-800'
                }`}
            >
                {statusLabels[status] || status}
            </span>
        );
    };

    return (
        <>
            <Card extra={'w-full px-4 py-4 border-2'}>
                <ToastContainer />
                {/* Header */}
                <div className="w-full px-1">
                    <h4 className="text-base font-bold text-navy-700 dark:text-white">
                        Information de facturation
                    </h4>
                </div>
            {/* inputs */}
            <div className="mt-4 grid grid-cols-2 gap-2">
                <InputField
                    extra="mb-2"
                    label="Mon entreprise"
                    placeholder="Mon entreprise"
                    defaultValue={user?.company}
                    id="company"
                    type="text"
                    onChange={handleChange}
                />
                <InputField
                    extra="mb-2"
                    label="Adresse"
                    placeholder="17 rue de la paix, 75000 Paris France"
                    defaultValue={user?.address}
                    id="address"
                    type="text"
                    onChange={handleChange}
                />
                <InputField
                    extra="mb-2"
                    label="Numéro de SIRET"
                    placeholder="12345678901234"
                    defaultValue={user?.siren}
                    id="siren"
                    type="text"
                    onChange={handleChange}
                />
            </div>
            <div className="mt-3 flex w-full justify-end">
                <button
                    className="linear rounded-lg bg-leadrs px-4 py-1.5 text-sm font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                    onClick={() => updateProfil()}
                >
                    Sauvegarder
                </button>
            </div>
            </Card>

            {/* Invoices List */}
            <Card extra={'w-full mt-4 px-4 py-4 border-2'}>
                <div className="w-full px-1">
                    <div className="flex items-center justify-between">
                        <h4 className="text-base font-bold text-navy-700 dark:text-white">
                            Mes factures
                        </h4>
                        <button
                            className="linear rounded-lg bg-leadrs px-3 py-1.5 text-xs font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                            onClick={() => fetchInvoices()}
                            disabled={loadingInvoices}
                        >
                            {loadingInvoices ? 'Chargement...' : 'Actualiser'}
                        </button>
                    </div>
                </div>

                <div className="mt-4">
                    {loadingInvoices ? (
                        <div className="flex justify-center py-4">
                            <p className="text-sm text-gray-600">Chargement des factures...</p>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 px-4">
                            {!user?.company || !user?.address || !user?.siren ? (
                                <div className="text-center max-w-md">
                                    <div className="mb-3 text-4xl">📋</div>
                                    <p className="text-xl font-medium text-navy-700 dark:text-white mb-2">
                                        Complétez vos informations de facturation
                                    </p>
                                    <p className="text-md text-gray-600 dark:text-gray-400">
                                        Veuillez renseigner vos informations d'entreprise ci-dessus (nom, adresse, SIRET) pour recevoir vos factures automatiquement après chaque commande.
                                    </p>
                                </div>
                            ) : (
                                <div className="text-center max-w-md">
                                    <div className="mb-3 text-4xl">✨</div>
                                    <p className="text-xl font-medium text-navy-700 dark:text-white mb-2">
                                        Prêt à recevoir vos factures
                                    </p>
                                    <p className="text-md text-gray-600 dark:text-gray-400">
                                        Vos informations de facturation sont complètes. Votre première facture sera générée automatiquement après votre première commande.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Numéro
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Date
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Montant
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Statut
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice: any) => (
                                        <tr
                                            key={invoice.id}
                                            className="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-navy-700"
                                        >
                                            <td className="px-3 py-2 text-xs text-navy-700 dark:text-white">
                                                {invoice.number || invoice.id.substring(0, 12)}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                                                {formatDate(invoice.created)}
                                            </td>
                                            <td className="px-3 py-2 text-xs font-medium text-navy-700 dark:text-white">
                                                {invoice.amount_due.toFixed(2)} {invoice.currency.toUpperCase()}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {getStatusBadge(invoice.status)}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                <div className="flex gap-2">
                                                    {invoice.hosted_invoice_url && (
                                                        <a
                                                            href={invoice.hosted_invoice_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-leadrs hover:text-brand-600 underline"
                                                        >
                                                            Voir
                                                        </a>
                                                    )}
                                                    {invoice.invoice_pdf && (
                                                        <a
                                                            href={invoice.invoice_pdf}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-leadrs hover:text-brand-600 underline"
                                                        >
                                                            PDF
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
};

export default UpdateBuyerInvoiceInformation;
